import * as React from 'react';
import type { IListPageLayoutListLabels, IListPageLayoutListProps } from './list-page-layout.types';
import { Body2Typography, LoadingSpinnerColor, LoadingSpinnerSize } from '@components/atoms';
import { LoadingSpinner } from '@components/atoms/loading-spinner/loading-spinner';
import { cn } from '@utils';

interface IListPageLayoutListStatusProps {
	isLoading?: boolean;
	isEmpty?: boolean;
	error?: Error | null;
	hasMore?: boolean;
	labels?: IListPageLayoutListLabels;
}

const ListPageLayoutListStatus: React.FC<IListPageLayoutListStatusProps> = ({
	isLoading,
	isEmpty,
	error,
	hasMore,
	labels,
}) => {
	if (isLoading) {
		return (
			<div className="flex w-full justify-center py-4" data-testid="list-page-layout-list-loading">
				<LoadingSpinner size={LoadingSpinnerSize.MD} color={LoadingSpinnerColor.PRIMARY} />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex w-full justify-center py-4" data-testid="list-page-layout-list-error">
				<Body2Typography className="text-primary" truncate={false}>
					{labels?.error ?? error.message}
				</Body2Typography>
			</div>
		);
	}

	if (isEmpty && labels?.empty) {
		return (
			<div className="flex w-full justify-center py-4" data-testid="list-page-layout-list-empty">
				<Body2Typography className="text-primary" truncate={false}>
					{labels.empty}
				</Body2Typography>
			</div>
		);
	}

	if (!hasMore && labels?.endOfPage) {
		return (
			<div className="flex w-full justify-center py-4" data-testid="list-page-layout-list-end">
				<Body2Typography className="text-primary" truncate={false}>
					{labels.endOfPage}
				</Body2Typography>
			</div>
		);
	}

	return null;
};

export const ListPageLayoutList = React.forwardRef<HTMLDivElement, IListPageLayoutListProps>(
	({ className, children, isLoading, isEmpty, error, hasMore, labels, ...props }, ref) => (
		<div
			ref={ref}
			className={cn('flex flex-col gap-3 w-full', className)}
			data-testid="list-page-layout-list"
			{...props}
		>
			{children}
			<ListPageLayoutListStatus
				isLoading={isLoading}
				isEmpty={isEmpty}
				error={error}
				hasMore={hasMore}
				labels={labels}
			/>
		</div>
	)
);
ListPageLayoutList.displayName = 'ListPageLayoutList';
